<template>
  <b-row class="d-flex py-3 mr-2 filters">
    <b-col class="align-self-center column-1">
      <b-form-radio-group v-model="dateRangeLocal"
                          buttons
                          button-variant="outline-teal"
                          class="text-nowrap radio-buttons range-select"
                          size="sm"
                          :options="ranges"
      />
    </b-col>
    <b-col class="align-self-center column-2">
      <b-form-radio-group v-model="intervalLocal"
                          :options="intervalLabels"
                          size="sm"
                          class="interval-select"
      />
    </b-col>
    <b-col class="align-self-center column-3">
      <b-input-group class="date-range-display" size="sm">
        <b-form-input class="mr-2"
                      :value="rangeDisplay.start"
                      disabled />
        <span>to</span>
        <b-form-input class="ml-2"
                      :value="rangeDisplay.end"
                      disabled />
      </b-input-group>

    </b-col>
    <b-col class="ml-4 column-4" ref="slider">
      <div class="slider">
        <vue-slider v-model="sliderStartDate"
                    :dotSize="sliderSize"
                    tooltip="none"
                    :data="sliderData"
                    @change="onSlide"
                    :disabled="!canScroll"
        />
      </div>
    </b-col>
  </b-row>
</template>

<script>
import VueSlider from 'vue-slider-component';
import 'vue-slider-component/theme/default.css';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
dayjs.extend(quarterOfYear);
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
dayjs.extend(isSameOrBefore);
import advancedFormat from 'dayjs/plugin/advancedFormat';
dayjs.extend(advancedFormat);
import { cloneDeep } from 'lodash';

export default {
  name: "DateRangeFilters",
  components: {
    VueSlider,
  },
  props: {
    dataSize: {
      type: Number,
      required: true,
    },
    interval: {
      type: String,
      required: true,
    },
    displayDataRange: {
      type: [Number, String],
      required: true,
    },
    startDate: {
      type: Object,
      required: true,
    },
    endDate: {
      type: Object,
      required: true,
    },
    rangeStartDate: {
      type: Object,
      required: true,
    },
    rangeEndDate: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      ranges: [
        { text: '6M', value: 5 },
        { text: '1Y', value: 11 },
        { text: '2Y', value: 23 },
        { text: '3Y', value: 35 },
        /*{ text: 'All', value: 'all' },*/
      ],
      intervalLabels: [
        { text: 'Months', value: 'month' },
        { text: 'Quarters', value: 'quarter' },
      ],
      selectedAxisLabel: null,
      sliderStartDate: null,
      dateRangeLocal: 11,
      intervalLocal: 'month',
      sliderWidth: null,
    }
  },
  computed: {
    sliderSize() {
      return 40;
    },
    getSelectedRangeValue() {
      return (this.dateRangeLocal === 'all') ? this.dataSize : this.dateRangeLocal;
    },
    sliderData() {
      let intervalDate = cloneDeep(this.startDate);
      const sliderData = [];
      const endDate = this.endDate.subtract(this.getSelectedRangeValue, 'month');

      while (intervalDate.isSameOrBefore(endDate)) {
        sliderData.push(intervalDate.format('YYYY-MM-DD'));
        intervalDate = intervalDate.add(1, this.intervalLocal);
      }

      return sliderData;

    },
    rangeDisplay() {
      const start = (this.intervalLocal === 'quarter')
          ? 'Q' + this.rangeStartDate.format('Q YYYY')
          : this.rangeStartDate.format('MMM YYYY');
      const end = (this.intervalLocal === 'quarter')
          ? 'Q' + this.rangeEndDate.format('Q YYYY')
          : this.rangeEndDate.format('MMM YYYY');
      return {
        start: start,
        end: end,
      }
    },
    canScroll() {
      const dataSize = (this.interval === 'quarter') ? this.dataSize*4 : this.dataSize;
      if ((this.getSelectedRangeValue + 1) < dataSize) {
        return true;
      }
      return false;
    },
  },
  created() {
    this.dateRangeLocal = this.displayDataRange;
    this.intervalLocal = this.interval;
  },
  mounted() {
    this.sliderWidth = this.$refs.slider.clientWidth;
  },
  watch: {
    displayDataRange: {
      handler(newValue) {
        this.dateRangeLocal = newValue;
      },
    },
    dateRangeLocal: {
      handler(newValue) {
        const reset = (newValue === 'all');
        if (reset) {
          this.sliderStartDate = null;
        } else {
          let index = this.sliderData.indexOf(this.sliderStartDate);
          // if not value set, set to 0 as teh slider has not been used
          if (this.sliderStartDate === null) {
            index = 0;
          }
          if (index < 0) {
            index = this.sliderData.length - 1;
          }

          this.sliderStartDate = this.sliderData[index];
        }

        this.$emit(
            'update:displayDataRange',
            { duration: this.getSelectedRangeValue, reset: reset, value: newValue }
        );
      },
      immediate: false,
    },
    intervalLocal: {
      handler(newValue) {
        this.$emit('update:interval', newValue);
      },
      immediate: false,
    },
  },
  methods: {
    onSlide(value, index) {
      const startDate = dayjs(value);
      const endDate = startDate.add(this.getSelectedRangeValue, 'month');

      this.$emit('update:displayDataRange', { startDate: startDate, endDate: endDate });
    },
  }
}
</script>

<style lang="scss">
  .column-1 {
    min-width: 200px;
    max-width: 230px;
  }
  .column-2 {
    max-width: 200px;
  }
  .column-3 {
    min-width: 210px;
    max-width: 210px;
  }
  .column-4 {
    min-width: 300px;
  }
  .text-small {
    font-size: 0.65rem;
  }
  .filters {
    .range-select {
      color: #000;
      label.btn {
        font-size: 0.65rem;
        line-height: 20px;
        padding-left: 15px;
        padding-right: 15px;
        cursor: pointer;
        background: #FFF;
        border-color: #CCD9E2;
        color: #2C3E38;
        &.active {
          background: #F7F9FA;
          border-color: #CCD9E2;
        }
      }
    }
    .slider {
      .vue-slider-process {
        background: transparent !important;
      }
      .vue-slider-dot {
        height: 18px !important;
        .vue-slider-dot-handle {
          border-radius: 10px;
        }
      }
    }
    .interval-select {
      .custom-control-input:focus ~ .custom-control-label::before {
        box-shadow: inset 0 -1px 0 #ddd;
      }
      label {
        color: #000000;
        font-size: 0.65rem;
        span {
          line-height: 24px;
        }
      }
    }
    .date-range-display {
      color: #000;
      max-width: 85%;
      align-items: center;
      input {
        background: #FFF !important;
        font-size: 0.65rem !important;
        text-transform: uppercase !important;
      }
      span {
        font-size: 0.65rem;
        line-height: 30px;
      }
    }
  }

</style>
